import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight, BookOpen, Clock, Calendar } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { ARTICLES } from '../data/articles.jsx';
import SEO from '../seo/SEO';

const CATEGORIES = ['All', 'Fundamentals', 'Pricing', 'Operations', 'Shipping', 'Brand'];

export default function Blog() {
  const [query, setQuery] = useState('');
  const [activeCat, setActiveCat] = useState('All');

  const filtered = useMemo(() => {
    let items = ARTICLES;
    if (activeCat !== 'All') items = items.filter((a) => a.category === activeCat);
    if (query.trim()) {
      const q = query.toLowerCase();
      items = items.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.excerpt.toLowerCase().includes(q) ||
          a.category.toLowerCase().includes(q)
      );
    }
    return items;
  }, [query, activeCat]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEO
        title="Ecommerce Fulfillment & 3PL Resources"
        description="Plain-English guides for ecommerce founders: how 3PL pricing actually works, shipping zones, onboarding checklists, packaging ROI, and when to switch fulfillment partners."
        keywords="3PL guide, fulfillment guide, ecommerce shipping, 3PL pricing, shipping zones, 3PL onboarding, custom packaging, switching 3PLs"
      />
      <Header />

      {/* Hero */}
      <section className="relative pt-16 sm:pt-20 pb-10 overflow-hidden">
        <div className="absolute inset-0 -z-10 brand-soft-bg" />
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-indigo-200/30 blur-3xl" />
          <div className="absolute top-32 -left-32 w-[360px] h-[360px] rounded-full bg-blue-200/25 blur-3xl" />
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-indigo-100 text-indigo-700 text-xs font-bold uppercase tracking-widest shadow-sm">
            <BookOpen className="w-3.5 h-3.5" /> Resources
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Straight answers on{' '}
            <span className="brand-gradient-text">fulfillment, pricing, and the work behind a brand.</span>
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl">
            Written by operators, for operators. No fluff, no buzzwords, no gated PDFs.
          </p>

          {/* Search bar */}
          <div className="mt-9 max-w-xl relative">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search articles..."
              className="h-13 pl-14 pr-4 text-base rounded-2xl bg-white border-slate-200 shadow-sm focus-visible:ring-2 focus-visible:ring-indigo-500"
            />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-2">
            {CATEGORIES.map((c) => {
              const active = activeCat === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setActiveCat(c)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all duration-150 ${
                    active
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-indigo-300 hover:text-indigo-600'
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Articles list */}
      <section className="pb-20 sm:pb-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {filtered.length === 0 ? (
            <div className="text-center py-20 bg-slate-50/60 border border-dashed border-slate-200 rounded-2xl">
              <p className="text-slate-600">No articles match your search.</p>
            </div>
          ) : (
            <>
              {featured && <FeaturedCard article={featured} />}
              {rest.length > 0 && (
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                  {rest.map((a) => (
                    <ArticleCard key={a.slug} article={a} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-indigo-50 border border-indigo-100 rounded-3xl p-10 sm:p-14 text-center">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              Want to see what your store would actually save?
            </h2>
            <p className="mt-3 text-lg text-slate-600 max-w-xl mx-auto">
              Get a free, no-pressure estimate in 30 seconds. No call required.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/zones#instant-estimate"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-md hover:shadow-lg transition-all duration-200"
              >
                Estimate My Savings
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/#pricing"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-slate-300 text-slate-800 hover:border-indigo-500 hover:text-indigo-600 font-semibold transition-colors"
              >
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function FeaturedCard({ article }) {
  return (
    <Link
      to={`/blog/${article.slug}`}
      className="group block rounded-3xl overflow-hidden border border-slate-200 bg-white hover:border-indigo-300 hover:shadow-xl transition-all duration-200"
    >
      <div className="grid grid-cols-1 lg:grid-cols-5">
        <div className="lg:col-span-3 brand-gradient relative p-10 sm:p-14 text-white overflow-hidden">
          <div
            className="absolute inset-0 opacity-15 pointer-events-none"
            style={{
              backgroundImage:
                'repeating-linear-gradient(135deg, rgba(255,255,255,0.4) 0px, rgba(255,255,255,0.4) 1px, transparent 1px, transparent 22px)',
            }}
            aria-hidden="true"
          />
          <div className="relative">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 text-white text-xs font-bold uppercase tracking-widest backdrop-blur-sm">
              Featured · {article.category}
            </span>
            <h2 className="mt-5 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight">
              {article.title}
            </h2>
            <p className="mt-4 text-indigo-100 text-base sm:text-lg leading-relaxed">
              {article.excerpt}
            </p>
            <div className="mt-5 flex items-center gap-4 text-sm text-indigo-100">
              <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {article.date}</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {article.readTime}</span>
            </div>
          </div>
        </div>
        <div className="lg:col-span-2 p-10 sm:p-12 flex flex-col justify-center">
          <p className="text-sm font-bold uppercase tracking-widest text-indigo-600">Latest read</p>
          <p className="mt-3 text-slate-700 text-lg leading-relaxed">
            Practical, plain-English thinking on the topics that actually move your fulfillment numbers.
          </p>
          <span className="mt-6 inline-flex items-center gap-2 text-indigo-600 font-bold group-hover:gap-3 transition-all">
            Read article <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function ArticleCard({ article }) {
  return (
    <Link
      to={`/blog/${article.slug}`}
      className="group block bg-white border border-slate-200 rounded-2xl p-7 hover:border-indigo-300 hover:shadow-lg transition-all duration-200"
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-bold uppercase tracking-widest">
          {article.category}
        </span>
        <span className="text-xs text-slate-400 font-medium inline-flex items-center gap-1">
          <Calendar className="w-3 h-3" /> {article.date}
        </span>
        <span className="text-xs text-slate-400 font-medium inline-flex items-center gap-1">
          <Clock className="w-3 h-3" /> {article.readTime}
        </span>
      </div>
      <h3 className="text-xl font-extrabold text-slate-900 leading-snug group-hover:text-indigo-700 transition-colors">
        {article.title}
      </h3>
      <p className="mt-2.5 text-slate-600 leading-relaxed">{article.excerpt}</p>
      <span className="mt-5 inline-flex items-center gap-1.5 text-indigo-600 font-semibold text-sm group-hover:gap-2.5 transition-all">
        Read article <ArrowRight className="w-4 h-4" />
      </span>
    </Link>
  );
}
